import { useEffect, useMemo, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import { AttendanceLocal } from "../../../../../../../../../@types/attendance-local";
import { useAttendanceLocalUserList } from "../../../../../../../../../services/api-tansquery/attendance-local/user/list";
import { AttendanceLocalUser } from "../../../../../../../../../@types/attendance-local-user";
import { useDebouncedValue } from "../../../../../../../../../hooks/useDebounce";
import { AttendanceLocalGuiche } from "../../../../../../../../../@types/attendance-local-guiche";

type Props = {
    slug: AttendanceLocal["slug_unidade"];
    field: ControllerRenderProps<{
        unidade?: string;
        ordem?: string;
        tipo?: string;
        ativo?: boolean;
        users?: string[];
    }, "users">
    fieldState: ControllerFieldState;
    users?: AttendanceLocalGuiche["user"];
    style?: React.CSSProperties;
}

export default function UsersField({ slug, field, fieldState, style, users }: Props) {
    const [search, setSearch] = useState<string>();
    const [selected, setSelected] = useState<Array<AttendanceLocalUser["user"]>>([]);
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data, isLoading } = useAttendanceLocalUserList({
        page: 1,
        unidade: slug,
        order_by: "-created_at",
        search: debouncedSearch,
    });

    const userList = useMemo(() => {
        return data?.results.map(item => item.user) || [];
    }, [data]);

    const options = useMemo(() => {
        const merged = [...selected, ...userList];
        return merged.filter(
            (item, index, self) => self.findIndex(i => i.username === item.username) === index
        );
    }, [userList, selected]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value === "" ? undefined : value);
        }
    }

    function handleChange(_, newValue: AttendanceLocalUser["user"][]) {
        setSearch(undefined);
        setSelected(newValue);
        field.onChange(newValue.map(item => item.username));
    }

    useEffect(() => {
        if (users) {
            setSelected(users);
        }
    }, [users]);

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            style={style}
            freeSolo={false}
            options={options}
            value={selected}
            inputValue={search || ""}
            getOptionLabel={(option) => `${option.first_name} - ${option.username}` || ""}
            getOptionSelected={(option, value) => option.username === value.username}
            onChange={handleChange}
            onInputChange={handleInputChange}
            loading={isLoading}
            loadingText="Carregando usuários..."
            noOptionsText="Nenhum usuário encontrado"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Responsáveis"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Selecione os responsáveis"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
